/**
 * Decoupled OTK (one-time-key) error monitor for AC-013.
 *
 * The matrix-js-sdk re-queues outgoing key uploads on the next sync, so an isolated
 * `/keys/upload` 400 is harmless and must never trigger a logout (see Wave 1, Decision 1).
 * This module observes those errors purely for UX: when they repeat past a threshold
 * inside a short window, the app surfaces an info toast. It carries no auth side effects.
 *
 * Detection lives in Faro's `beforeSend` (telemetry is observe-only — see architecture.md
 * "Boundaries"), which dispatches `OTK_ERROR_EVENT`. The counting + toast decision live in
 * `useCryptoErrorToast`, on the React side, so this stays a plain testable sliding window.
 */

const WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const THRESHOLD = 3;
const timestamps: number[] = [];

/** Custom DOM event dispatched when an OTK upload error is observed in telemetry. */
export const OTK_ERROR_EVENT = 'meedio:matrix-otk-error';

const OTK_MESSAGE_PATTERN = /one[\s_-]?time[\s_-]?key/i;

/**
 * True when a telemetry log message looks like a Matrix OTK upload error
 * (e.g. `MatrixError: [400] One time key ... already exists`).
 */
export const isOtkUploadError = (message: unknown): boolean =>
  typeof message === 'string' && message.includes('MatrixError') && OTK_MESSAGE_PATTERN.test(message);

/** Notify subscribers that an OTK upload error was observed. No auth side effects. */
export const dispatchOtkErrorEvent = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OTK_ERROR_EVENT));
};

/** Record an OTK error and report whether the threshold has been reached within the window. */
export const recordOtkError = (): boolean => {
  const now = Date.now();
  while (timestamps.length && now - timestamps[0] > WINDOW_MS) timestamps.shift();
  timestamps.push(now);
  return timestamps.length >= THRESHOLD;
};

/** Current OTK error count within the sliding window (after pruning stale entries). */
export const getOtkCountInWindow = (): number => {
  const now = Date.now();
  while (timestamps.length && now - timestamps[0] > WINDOW_MS) timestamps.shift();
  return timestamps.length;
};

/** Clear the sliding window (used on recovery and in tests). */
export const resetOtkErrorMonitor = (): void => {
  timestamps.length = 0;
};
